import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../services/supabase';
import type { User } from '../types';
import toast from 'react-hot-toast';

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    isLoggingIn: boolean; // Flag to prevent SIGNED_OUT during login

    // Actions
    login: (email: string, password: string) => Promise<boolean>;
    loginWithCompanyCode: (companyCode: string) => Promise<boolean>;
    loginWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    checkSession: () => Promise<void>;
    setUser: (user: User) => void;
    refreshUserProfile: () => Promise<void>;
    subscribeToProfileChanges: () => void;
    unsubscribeFromProfileChanges: () => void;
}

// İzinli Yöneticiler (Google Login için)
export const ALLOWED_ADMIN_EMAILS = [
    'admin@cheesecake.com',
    'yonetim@cheesecake.com',
    'punkagario123@gmail.com',
    // Test için kendi mail adresinizi buraya ekleyebilirsiniz
    'zeyne@gmail.com'
];

// Realtime subscription channel
let profileChannel: any = null;

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    isLoggingIn: false,

    login: async (email: string, password: string) => {
                set({ isLoading: true, error: null });

                try {
                    const { data, error } = await supabase.auth.signInWithPassword({
                        email,
                        password,
                    });

                    if (error) throw error;

                    if (data.user) {
                        // Profil bilgisini çek
                        const { data: profile, error: profileError } = await supabase
                            .from('profiles')
                            .select('*')
                            .eq('id', data.user.id)
                            .single();

                        if (profileError) throw profileError;

                        const user: User = {
                            id: data.user.id,
                            email: data.user.email || '',
                            firstName: profile.first_name || '',
                            lastName: profile.last_name || '',
                            fullName: `${profile.first_name} ${profile.last_name}`,
                            companyName: profile.company_name || '',
                            companyCode: profile.company_code,
                            phone: profile.phone || '',
                            phoneVerified: profile.phone_verified || false,
                            role: profile.role || 'customer',
                            addresses: [], // Adresler ayrıca çekilmeli ama şimdilik boş
                            createdAt: new Date(profile.created_at),
                        };

                        set({
                            user,
                            token: data.session?.access_token || null,
                            isAuthenticated: true,
                            isLoading: false,
                        });
                        return true;
                    }
                    return false;
                } catch (error: any) {
                    set({ isLoading: false, error: error.message });
                    return false;
                }
            },

            loginWithCompanyCode: async (companyCode: string) => {
                set({ isLoading: true, error: null });

                try {
                    console.log('[Auth] Attempting company code login:', companyCode);
                    
                    // Direkt profiles tablosundan firma kodunu kontrol et
                    // Supabase Auth kullanmadan giriş yap
                    const { data: profile, error: profileError } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('company_code', companyCode.trim().toUpperCase())
                        .eq('role', 'customer')
                        .eq('is_active', true)
                        .single();

                    console.log('[Auth] Profile query result:', { profile, error: profileError });

                    if (profileError || !profile) {
                        throw new Error('Firma numarası hatalı veya firma bulunamadı.');
                    }

                    // Kullanıcı bilgilerini oluştur
                    const user: User = {
                        id: profile.id,
                        email: profile.email || '',
                        firstName: profile.first_name || profile.full_name?.split(' ')[0] || '',
                        lastName: profile.last_name || profile.full_name?.split(' ').slice(1).join(' ') || '',
                        fullName: profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
                        companyName: profile.company_name || '',
                        companyCode: profile.company_code,
                        phone: profile.phone || '',
                        phoneVerified: profile.phone_verified || false,
                        role: profile.role || 'customer',
                        tier: profile.tier || 'Bronze',
                        discountRate: profile.discount_rate || 0,
                        addresses: [],
                        createdAt: new Date(profile.created_at),
                    };

                    console.log('[Auth] Login successful, user:', user.email);

                    set({
                        user,
                        token: `company-${companyCode}`, // Pseudo token for session management
                        isAuthenticated: true,
                        isLoading: false,
                    });
                    return true;
                } catch (error: any) {
                    console.error('[Auth] Login failed:', error);
                    set({ isLoading: false, error: error.message });
                    return false;
                }
            },

            loginWithGoogle: async () => {
                set({ isLoading: true, error: null });
                try {
                    // redirectTo: production https://b2b.cheesecakeistanbul.com/auth/callback
                    // Supabase Dashboard → Authentication → URL Configuration → Redirect URLs listesine ekleyin
                    const { error } = await supabase.auth.signInWithOAuth({
                        provider: 'google',
                        options: {
                            redirectTo: typeof window !== 'undefined'
                                ? `${window.location.origin}/auth/callback`
                                : undefined,
                            queryParams: {
                                access_type: 'offline',
                                prompt: 'consent',
                            },
                        },
                    });
                    if (error) throw error;
                    // OAuth yönlendirmesi olduğu için burada set state yapmaya gerek yok,
                    // dönüşte checkSession yakalayacak.
                } catch (error: any) {
                    set({ isLoading: false, error: error.message });
                }
            },

            logout: async () => {
                try {
                    await supabase.auth.signOut();
                } catch (e) {
                    console.error('Supabase signOut failed:', e);
                }

                // State'i sıfırla
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                });

                // Local Storage'ı temizle (setup ve tercihler korunur)
                try {
                    // Önce korunacak değerleri sakla
                    const setupComplete = localStorage.getItem('cheesecake-setup-complete');
                    const userPreferences = localStorage.getItem('cheesecake-preferences');
                    const themeSettings = localStorage.getItem('cheesecake-theme');
                    const userSettings = localStorage.getItem('cheesecake-user-settings');

                    // Temizle
                    localStorage.removeItem('cheesecake-auth');
                    localStorage.removeItem('sb-cheesecake-auth-token'); // Supabase token
                    sessionStorage.clear();

                    // Diğer tüm auth ilgili itemları temizle
                    const keysToRemove: string[] = [];
                    for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        if (key && (key.startsWith('sb-') || key.includes('auth') || key.includes('token') || key.includes('session'))) {
                            keysToRemove.push(key);
                        }
                    }
                    keysToRemove.forEach(key => localStorage.removeItem(key));

                    // Korunan değerleri geri yükle (zaten silinmiş olabilirler)
                    if (setupComplete) localStorage.setItem('cheesecake-setup-complete', setupComplete);
                    if (userPreferences) localStorage.setItem('cheesecake-preferences', userPreferences);
                    if (themeSettings) localStorage.setItem('cheesecake-theme', themeSettings);
                    if (userSettings) localStorage.setItem('cheesecake-user-settings', userSettings);
                } catch (e) {
                    console.error('Storage clear failed:', e);
                }
            },

            checkSession: async () => {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user) {
                    // Session varsa user state'ini güncelle (isteğe bağlı)
                    // Şimdilik sadece token refresh işlemi otomatik oluyor
                } else {
                    // Session yoksa çıkış yap
                    get().logout();
                }
            },

            setUser: (user: User) => {
                set({
                    user,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                });
            },

            // Profil bilgilerini yenile (üyelik seviyesi değişikliklerini almak için)
            refreshUserProfile: async () => {
                const currentUser = get().user;
                if (!currentUser) return;

                try {
                    const { data: profile, error } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('id', currentUser.id)
                        .single();

                    if (error) throw error;

                    if (profile) {
                        const updatedUser: User = {
                            ...currentUser,
                            tier: profile.tier || currentUser.tier,
                            discountRate: profile.discount_rate || 0,
                            companyName: profile.company_name || currentUser.companyName,
                            fullName: profile.full_name || currentUser.fullName,
                            phone: profile.phone || currentUser.phone,
                        };

                        set({ user: updatedUser });
                    }
                } catch (error) {
                    console.error('Profile refresh failed:', error);
                }
            },

            // Profil değişikliklerini real-time dinle
            subscribeToProfileChanges: () => {
                const currentUser = get().user;
                if (!currentUser) return;

                // Önceki subscription varsa temizle
                if (profileChannel) {
                    supabase.removeChannel(profileChannel);
                }

                // Yeni subscription oluştur
                profileChannel = supabase
                    .channel(`profile-changes-${currentUser.id}`)
                    .on(
                        'postgres_changes',
                        {
                            event: 'UPDATE',
                            schema: 'public',
                            table: 'profiles',
                            filter: `id=eq.${currentUser.id}`
                        },
                        async (payload) => {
                            console.log('[Realtime] Profile updated:', payload);
                            
                            // Profili yenile
                            await get().refreshUserProfile();
                            
                            // Kullanıcıya bildirim göster
                            const newProfile = payload.new as any;
                            if (newProfile.tier !== currentUser.tier) {
                                toast.success(
                                    `Üyelik seviyeniz ${newProfile.tier} olarak güncellendi! 🎉`,
                                    { duration: 5000 }
                                );
                            } else if (newProfile.discount_rate !== currentUser.discountRate) {
                                toast.info(
                                    `İndirim oranınız %${newProfile.discount_rate} olarak güncellendi`,
                                    { duration: 4000 }
                                );
                            }
                        }
                    )
                    .subscribe((status) => {
                        console.log('[Realtime] Subscription status:', status);
                    });
            },

            // Subscription'ı kapat
            unsubscribeFromProfileChanges: () => {
                if (profileChannel) {
                    supabase.removeChannel(profileChannel);
                    profileChannel = null;
                    console.log('[Realtime] Unsubscribed from profile changes');
                }
            }
        }),
        {
            name: 'cheesecake-auth-storage',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);
